import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { dateIsAfter, dateDifference, selectDateFormattingLocaleForRegion } from 'lib/datetime/dateUtils'
import DateInput from 'components/Common/Form/Input/DateInput'
import GeoContext from 'contexts/geoContext'

interface Props extends Partial<React.ComponentProps<typeof DateInput>> {
  travellerType?: string
  refDate?: Date
}

const MAX_SENSIBLE_AGE = 150

export function getDobPlaceholder(locale: string): string {
  // there aren't really any packages out there that we can use for this
  // so we need custom helper
  const date = new Date(Date.UTC(2006, 10, 22))
  const placeholders: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {
    day: 'DD',
    month: 'MM',
    year: 'YYYY',
  }
  // oxlint-disable-next-line datetime-display/no-inline-intl -- derives input syntax; it does not format a displayed date.
  const order = new Intl.DateTimeFormat(locale, {
    calendar: 'gregory',
    day: '2-digit',
    month: '2-digit',
    numberingSystem: 'latn',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .formatToParts(date)
    .filter(({ type }) => type !== 'literal')
    .map(({ type }) => placeholders[type]!)

  return order.join('/')
}

const isFutureDate = (date: Date) => { return dateIsAfter(date, new Date()) }
const yearDifferenceFromNow = (date: Date | undefined, refDate: Date) => {
  const difference = dateDifference(refDate, date)
  return Math.round(difference.days / 365)
}
const monthDifferenceFromNow = (date: Date | undefined, refDate: Date) => {
  const difference = dateDifference(refDate, date)
  return Math.round(difference.days / 30)
}

function DateOfBirthInput(props: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { min, max, travellerType = 'adult', ...rest } = props
  const format = getDobPlaceholder(selectDateFormattingLocaleForRegion(currentRegionCode))

  const validationBounds = useMemo(() => {
    const refDate = props.refDate || new Date()
    return {
      upperAgeBound: yearDifferenceFromNow(min, refDate),
      lowerAgeBound: yearDifferenceFromNow(max, refDate),
      upperMonthsBound: monthDifferenceFromNow(min, refDate),
      lowerMonthsBound: monthDifferenceFromNow(max, refDate),
    }
  }, [max, min, props.refDate])

  const getInvalidMessage = useCallback((e: HTMLInputElement): string | undefined => {
    if (e.validity.patternMismatch) { return undefined }

    // Read the entered date rather than validationMessage: DateInput swaps its signal codes for
    // localised copy, so validationMessage differs between the first and later validation passes
    const inputDate = moment(e.value, format, true)
    // DateInput shows its own localised "Invalid date" message for malformed dates
    if (!inputDate.isValid()) { return undefined }

    const inputDateValue = inputDate.toDate()
    if (isFutureDate(inputDateValue)) {
      return intl.formatMessage({
        id: 'gLzjaEUhFtG9geYszbBr',
        defaultMessage: 'Cannot be in the future',
        description: 'Validation error when date of birth is in the future',
      })
    }

    const { upperAgeBound, lowerAgeBound, upperMonthsBound, lowerMonthsBound } = validationBounds

    let message: string | undefined
    if (e.validity.customError && e.validationMessage) {
      // Error message for traveller that is an adult or a child (with adult pricing)
      if (travellerType === 'adult' || (travellerType === 'child' && upperAgeBound === MAX_SENSIBLE_AGE)) {
        const isTooYoung = !!max && inputDate.isAfter(max)
        message = isTooYoung
          ? intl.formatMessage(
              {
                id: '5cI3_r1V_talNdnlaZxb',
                defaultMessage: 'Must be over {age} years old',
                description: 'Validation error when traveller is younger than the minimum required age',
              },
              { age: lowerAgeBound },
            )
          : intl.formatMessage(
              {
                id: 'V5oBKgoWrNS7CzJhIyl0',
                defaultMessage: 'Must be {age} years old or younger',
                description: 'Validation error when traveller is older than the maximum allowed age for the selected date of birth input',
              },
              { age: upperAgeBound },
            )
      } else if (lowerMonthsBound > 0 && lowerMonthsBound < 12 && upperAgeBound) {
        message = intl.formatMessage(
          {
            id: 'A-2eUUT9eXiXoYH3bYsL',
            defaultMessage: 'Must be between {months, plural, one {# month} other {# months}}-{years} years old',
            description: 'Age validation when minimum age is in months and maximum is in years',
          },
          { months: lowerMonthsBound, years: upperAgeBound },
        )
      } else if (lowerAgeBound || upperAgeBound) {
        message = intl.formatMessage(
          {
            id: 'NZZkgTq8qxciNi2ePtTR',
            defaultMessage: 'Must be between {lower}-{upper} years old',
            description: 'Age validation for travellers within a year range in the input section',
          },
          { lower: lowerAgeBound, upper: upperAgeBound },
        )
      } else {
        message = intl.formatMessage(
          {
            id: 'TZ-pVZ0ceG1mmcBFe3mo',
            defaultMessage: 'Must be between {lower}-{upper} months old',
            description: 'Age validation for infants within a month range in the input section',
          },
          { lower: lowerMonthsBound, upper: upperMonthsBound },
        )
      }
    }
    return message
  }, [format, intl, max, travellerType, validationBounds])

  return (
    <DateInput
      {...rest}
      min={min}
      max={max}
      displayFormat={format}
      placeholder={format}
      getInvalidMessage={getInvalidMessage}
    />
  )
}

export default DateOfBirthInput
